import { Text } from '@thanx/uikit/text'
import type { AvailableReward } from 'api/recoveryAi.types'
import type { ChatMessage } from 'hooks/websocket/recoveryChatSource'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { composeAgentIntro } from 'scenes/RecoveryAI/components/agentIntro'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import ChatInput from './ChatInput'
import MessageBubble, { VisualRole } from './MessageBubble'
import SandboxRewardCard from './SandboxRewardCard'
import { useMerchantBrand } from './useMerchantBrand'

export type { ChatMessage }

const t = buildTranslate('recovery_ai.sandbox')
const tIntro = buildTranslate('recovery_ai.agent_introduction')

// Log padding. The Figma runs a 12px gutter, half of what shipped.
const LOG_PADDING_PX = 12
// Slack in px for "still at the bottom". Absorbs sub-pixel rounding and a
// fractional device-pixel ratio without letting a deliberate scroll-up re-pin.
const SCROLL_FOLLOW_THRESHOLD_PX = 32

type Props = {
  messages: ChatMessage[]
  currentStreamingMessage: string | null
  awaitingResponse: boolean
  isToolRunning: boolean
  onSendMessage: (text: string) => void
  assistantName?: string | null
  agentName?: string | null
  // When true, drop the panel's own card chrome (border/background/radius/min
  // height) — the caller supplies the frame (e.g. PhoneFrame in the wizard).
  bare?: boolean
  // Inline chat-offer (AI-584): the reward the agent resolved with, rendered
  // directly in-conversation (no "View offer" disclosure — reverses AI-516).
  // Optional/nullable so every existing call site keeps type-checking and no
  // offer renders until a resolution lands.
  offer?: AvailableReward | null
  // Terminal escalation: the agent routed the case to the team. Like a granted
  // reward, the AI has stopped listening, so the compose box locks. The
  // escalation MESSAGE arrives as a normal AI bubble in `messages`; this flag
  // only drives the input lock.
  escalated?: boolean
  /**
   * Scripted demo (R1-R3): the phone is an inert canned conversation the
   * merchant watches, not a live chat. Locks the compose box and makes the log a
   * focusable, separately-named scroll region. Default false — the live Agent
   * Tuning tab and the wizard's free phase are byte-identical without it (D4).
   *   context: the CONTENT is scripted; the DELIVERY is still progressive, so
   *   role="log" + aria-relevant="additions" stay correct on BOTH paths.
   */
  scriptedDemo?: boolean
  /**
   * Render `offer` immediately after the message with this id instead of at the
   * tail of the log — canonical invariant #4 puts the demo reward card
   * mid-conversation. Absent ⇒ today's trailing position; supplied but not yet
   * matched ⇒ the card is withheld rather than floated to the tail.
   */
  offerAfterMessageId?: string
  // The brand-voiced opening line from nexus canned responses (useSimulatorData's
  // `cannedGreeting`) — the same body the live consumer chat greets with.
  // Optional so existing call sites keep type-checking; undefined while it's
  // loading/unavailable falls back to the bundled template below.
  cannedGreeting?: string
  /**
   * The conversation is LIVE but the merchant may not drive it — the wizard's
   * guided phase sends the scripted customer turns itself
   * (useCannedConversation). Keeps the compose box mounted and disables it.
   *
   * Deliberately NOT `scriptedDemo`, which UNMOUNTS the box because its
   * conversation was never live at all. Here there is a real socket the merchant
   * simply isn't holding the wheel of, so the affordance stays visible and
   * explains itself by being greyed rather than vanishing.
   */
  composeLocked?: boolean
  /**
   * Keep the greeting on screen after the log fills.
   *
   * The greeting is presentation-only — deliberately never pushed into
   * `messages` (see resolveGreeting below) — so it is otherwise gated on an
   * empty log and disappears the moment anything is said. That is right when the
   * CUSTOMER speaks first, and wrong in the guided phase, where a turn the
   * wizard sent seconds ago would take the agent's opening line with it.
   */
  pinGreeting?: boolean
}

// Normalize raw ChatMessage role to one of two visual treatments the UI
// supports today. System messages render as AI-styled bubbles, so we
// group them with AI for the purpose of "did the sender change?".
const visualRole = (role: ChatMessage['role']): VisualRole =>
  role === 'user' ? VisualRole.User : VisualRole.Ai

/**
 * Whether to show the sender label above a message — only when it's the
 * first message or the previous message had a different *visual* sender, so a
 * run of consecutive agent messages carries one label rather than one each.
 *
 * Only ever consulted for the AGENT: the Figma labels the merchant's messages and
 * leaves the customer's unlabelled, so there is no "You" to collapse.
 */
function shouldShowLabel(messages: ChatMessage[], index: number): boolean {
  if (index === 0) return true
  return (
    visualRole(messages[index].role) !== visualRole(messages[index - 1].role)
  )
}

const ChatPanel = ({
  messages,
  currentStreamingMessage,
  awaitingResponse,
  isToolRunning,
  onSendMessage,
  assistantName,
  agentName,
  bare = false,
  offer,
  escalated = false,
  scriptedDemo = false,
  offerAfterMessageId,
  cannedGreeting,
  composeLocked = false,
  pinGreeting = false,
}: Props) => {
  const [css] = useStyletron()
  const scrollRef = useRef<HTMLDivElement>(null)
  // Starts pinned: an unscrolled log is at the bottom by definition, and jsdom
  // reports every dimension as 0, so tests inherit the following behaviour.
  const isPinnedToBottomRef = useRef(true)
  // ENG-977: the greeting names the merchant — the phone previews what the
  // CUSTOMER sees.
  const { name: merchantName } = useMerchantBrand()

  // The label over the agent's messages is the MERCHANT (D4, approved 2026-08-17) —
  // the Figma reads "PizzaCo", not "RecoveryAI", because the phone previews what the
  // customer sees and the greeting one line down already names the merchant.
  // `assistant_name` stays behind it for a merchant record that has not resolved.
  const aiName = merchantName || assistantName || t('default_assistant_name')

  // Presentation only: never pushed into `messages`, so the agent never sees it.
  const resolveGreeting = (): string => {
    if (cannedGreeting) return cannedGreeting
    return `${composeAgentIntro(agentName, merchantName)}\n\n${tIntro(
      'rest_fallback'
    )}`
  }
  const greeting = resolveGreeting()

  // Openers only: the greeting belongs to the un-started conversation and
  // disappears once the customer says something.
  const isConversationEmpty = messages.length === 0

  // Both playback modes strip the phone of every other tab stop: `scriptedDemo`
  // drops the compose box outright, and `composeLocked` disables the textarea
  // and Send. Either way the log is left as the only thing a keyboard-only
  // merchant can reach, so it has to be reachable.
  const logIsOnlyTabStop = scriptedDemo || composeLocked

  // Terminal states: the agent either granted a reward (AI-583) or escalated to
  // the team — in both, the AI has stopped listening, so lock the compose box +
  // send button and blank the placeholder so the customer can no longer type.
  // A scripted demo was never live, so it joins the same lock rather than adding
  // a second disabled path — this one boolean already gates the textarea, the
  // send button and the submit handler. It is the FIRST disjunct on purpose, so
  // R3 holds for the whole demo whether or not an offer has been revealed yet.
  const conversationEnded = scriptedDemo || !!offer || escalated

  // Sending is an explicit "take me to the bottom" gesture — every chat client
  // treats it that way, and useRecoveryChat appends the merchant's own message
  // optimistically, so without this a merchant who had scrolled up would watch
  // their message, the "..." bubble and the reply all land off-screen and read
  // it as "the send button did nothing".
  //
  //   context: re-pinned at the PORT rather than the call site, so future
  //   callers can't diverge. Anything that sends must go through here rather
  //   than calling onSendMessage directly.
  const handleSend = useCallback(
    (text: string) => {
      isPinnedToBottomRef.current = true
      onSendMessage(text)
    },
    [onSendMessage]
  )

  // Follow the log only while the reader is actually at the bottom. A scripted
  // scenario reveals a frame every 0.35-2s for up to 12s, and this same panel is
  // a tab stop with its own focus ring precisely so a keyboard user can scroll
  // back through it (WCAG 2.1.1) — an unconditional write would yank them to the
  // bottom within two seconds of every PageUp, and do the same to a mouse-wheel
  // reader on the live path.
  //
  //   context: the pin is measured on SCROLL, not in the effect. Appending a
  //   message grows scrollHeight while scrollTop stays put, so an effect-time
  //   measurement reads one message-height away from the bottom and would
  //   unpin itself on the first reveal. Programmatic writes below re-fire this
  //   handler and clamp to 0, so following stays sticky.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    isPinnedToBottomRef.current =
      distanceFromBottom <= SCROLL_FOLLOW_THRESHOLD_PX
  }, [])

  useEffect(() => {
    if (!scrollRef.current || !isPinnedToBottomRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    // The reward card and the "..." bubble are NOT members of `messages`, so
    // without them in the deps the log stays put when either appears — and the
    // card is the payoff of a scripted scenario (AI-921). Live behaviour changes
    // too: a live "..." now scrolls the log down, which is what a chat should do.
  }, [messages, currentStreamingMessage, awaitingResponse, offer])

  // Whether the streaming message needs a label (different visual sender
  // from the last completed message).
  const streamingNeedsLabel =
    messages.length === 0 ||
    visualRole(messages[messages.length - 1].role) !== VisualRole.Ai

  // Canonical invariant #4: the demo reward card sits BETWEEN messages, not
  // after them. The live path passes no anchor and keeps today's trailing
  // position (the backend emits resolution_data after the reply has streamed).
  const offerAnchorIndex = useMemo(() => {
    if (!offer || offerAfterMessageId == null) return -1
    return messages.findIndex(msg => msg.id === offerAfterMessageId)
  }, [offer, offerAfterMessageId, messages])

  const offerCard = offer ? (
    <div className={css({ marginBottom: '16px' })}>
      <SandboxRewardCard
        reward={offer}
        showRewardLink={scriptedDemo}
        variant="chat"
      />
    </div>
  ) : null

  return (
    <div
      className={css({
        backgroundColor: bare ? 'transparent' : 'white',
        border: bare ? 'none' : `1px solid ${merchantTheme.colors.grey20}`,
        borderRadius: bare ? '0' : '8px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: bare ? '0' : '500px',
      })}
    >
      {/* Message area. `role="log"` already implies polite live-region
          semantics for new additions; we narrow with `aria-relevant="additions"`
          so screen readers only announce newly-appended messages, not every
          mutation that happens during a streaming text_chunk update. The
          per-chunk streaming bubble is intentionally NOT live — we only
          announce completed messages. Transient status (thinking, tool
          running) goes through a separate `role="status"` region below. In
          either playback mode the log also becomes the phone's only tab stop
          (D11b).
            context: R3 drops the compose box and `composeLocked` disables it,
            so nothing inside the phone is focusable and a keyboard-only
            merchant could not scroll this log (WCAG 2.1.1). tabIndex={0} alone
            is right — browsers handle Arrow/PgUp/Home natively on a focused
            scroll container. The name swaps only under `scriptedDemo` because
            t('title') is internal jargon ("Conversation Sandbox") that would
            otherwise name that new tab stop, whereas a live-canned run really
            is the conversation that title describes. */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={css({
          flex: 1,
          // Let this flex child shrink below its content so overflowY:auto
          // actually scrolls inside a height-capped phone — the flex default of
          // min-height:auto would otherwise push the frame taller than its cap.
          minHeight: 0,
          overflowY: 'auto',
          padding: `${LOG_PADDING_PX}px`,
          ...(logIsOnlyTabStop
            ? {
                // Reading to the end of the transcript must not then scroll the
                // wizard page out from under the merchant (scroll chaining).
                overscrollBehavior: 'contain',
                // Focusable purely so it can be scrolled, with no other focus
                // treatment — make the ring explicit rather than UA-dependent.
                ':focus-visible': {
                  outline: `2px solid ${merchantTheme.colors.belizeHole50}`,
                  outlineOffset: '-2px',
                },
              }
            : {}),
        })}
        role="log"
        aria-label={
          scriptedDemo ? t('static_transcripts.region_label') : t('title')
        }
        aria-relevant="additions"
        tabIndex={logIsOnlyTabStop ? 0 : undefined}
      >
        {/* ENG-977: the assistant's opening greeting, shown before the customer
            has typed anything. No longer needs a `plain` flag — every agent
            message is unbubbled now, which is what the Figma shows. */}
        {(pinGreeting || isConversationEmpty) && (
          <MessageBubble
            role={VisualRole.Ai}
            content={greeting}
            senderLabel={aiName}
          />
        )}

        {/* Quick-reply suggestion pills (AI-166 / ENG-977) intentionally not
            rendered here — hidden to match the consumer chat, which dropped
            them first. QUICK_REPLIES config and the pill styling live in git
            history; this is a bare visibility change, not a removal of the
            feature. */}

        {messages.map((msg, index) => {
          const role = visualRole(msg.role)
          const isUser = role === VisualRole.User
          // BOTH speakers are labelled at every run boundary; only the agent's label
          // paints. The Figma has no visible "You" over the customer's own bubbles —
          // they are the one typing — but an agent-only label leaves the a11y tree with
          // no speaker change at all: MessageBubble carries no role/aria, the bubbles
          // differ only by background colour, and this collapses per run, so
          // [ai, ai, user, user] announced one label and then nothing (WCAG 1.3.1,
          // inside a role="log"). Hiding the customer's label rather than dropping it
          // satisfies the design and the a11y tree at once.
          const label = shouldShowLabel(messages, index)
            ? isUser
              ? t('customer_label_sr')
              : aiName
            : undefined
          return (
            <React.Fragment key={msg.id}>
              <MessageBubble
                role={role}
                content={msg.content}
                senderLabel={label}
                labelHidden={isUser}
              />
              {index === offerAnchorIndex && offerCard}
            </React.Fragment>
          )
        })}

        {/* Streaming message */}
        {currentStreamingMessage && (
          <MessageBubble
            role={VisualRole.Ai}
            content={currentStreamingMessage}
            senderLabel={streamingNeedsLabel ? aiName : undefined}
          />
        )}

        {/* Loading indicator. `...` reads as literal ellipsis to a screen
            reader, so the bubble carries an aria-label with the actual
            "thinking" copy. Single `role="status"` region so a screen reader
            announces "RecoveryAI is thinking…" once when it appears, instead
            of re-announcing the whole log.
              context: AI-1118 — the simulator now mirrors what the consumer
              sees, which has no tool-specific bubble. `isToolRunning` no
              longer renders its own indicator; it only keeps this generic one
              up while NOTHING has streamed yet. useRecoveryChat's `text_chunk`
              sets `currentStreamingMessage` without clearing `isToolRunning`,
              so once real text starts streaming (even if a tool call is still
              nominally running), `!currentStreamingMessage` goes false and
              this bubble renders nothing — the streamed text itself is the
              activity signal at that point, so no separate indicator shows. */}
        {(awaitingResponse || isToolRunning) && !currentStreamingMessage && (
          <div
            role="status"
            aria-label={t('ai_thinking')}
            className={css({
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'flex-start',
            })}
          >
            <div
              className={css({
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: merchantTheme.colors.grey05,
              })}
              aria-hidden="true"
            >
              <Text.BodyText3 color="grey50">...</Text.BodyText3>
            </div>
          </div>
        )}

        {/* Inline chat-offer (AI-584): the reward the agent resolved with,
            rendered DIRECTLY — no "View offer" disclosure (reverses AI-516) — at
            the bottom of the log so the terminal offer sits after the final
            message. The backend emits resolution_data after the reply has
            streamed (message_complete), so in the settled state the streaming/
            status bubbles above are empty and it reads directly after the last
            message; rendering it last also keeps order correct if a confirming
            message is still streaming.
              context: an ANCHORED offer renders ONLY at its anchor. Falling back
              to the tail would put the demo reward card above the conversation
              that earns it while the transcript is still playing in (AI-921).
              The live path passes no anchor, so `undefined === undefined` keeps
              today's trailing position. */}
        {offerAnchorIndex === -1 &&
          offerAfterMessageId === undefined &&
          offerCard}
      </div>

      {/* No compose box in the scripted demo at all (Aaron, 2026-08-18): the Figma
          gives the transcript the full phone, and a disabled box with a caption
          explaining why it is disabled was chrome answering a question the design
          never raises. The log carries tabIndex={0} in this mode precisely because
          it is then the only focusable thing in the phone, so nothing is stranded
          — and for the same reason under `composeLocked` below, where the box is
          mounted but every control in it is disabled.
            context: `conversationEnded` still takes scriptedDemo as a disjunct — it
            independently gates handleSend, which must stay inert during
            playback. Only the input is dropped. */}
      {!scriptedDemo && (
        <ChatInput
          // `composeLocked` is the live-canned guided phase: a real socket the
          // merchant is only watching. Unlike scriptedDemo above, the box stays
          // MOUNTED and greys out — the affordance is genuine, it is just not the
          // merchant's turn.
          disabled={awaitingResponse || conversationEnded || composeLocked}
          // Terminal (reward granted OR escalated) → blank the placeholder too (the
          // greyed box reads as "done"), unlike the transient awaitingResponse grey.
          terminal={conversationEnded}
          onSend={handleSend}
        />
      )}
    </div>
  )
}

export default ChatPanel
